import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ALTO_DE_FILA, ALTO_DE_PERSONA, aFecha, aIso, construirEje, diasHabilesEntre, sumarDias,
  zoomAnclado,
} from './ganttEje';
import {
  COLOR, colorDeCarga, colorDePrioridad, fechaDeducida, marcaDeCompromiso,
} from './ganttTheme';
import { assigneeLabel } from './ganttUtils';
import GanttBarraCard from './GanttBarraCard';
import GanttConflictos from './GanttConflictos';
import { MODO, cambioDelGesto, dedicacionParaDurar, useArrastre } from './ganttArrastre';
import { TIPOS_DE_VINCULO } from '@/lib/tiposDeVinculo';

const ANCHO_NOMBRES = 320;
/**
 * De que punta sale y a que punta llega cada tipo de vinculo.
 *
 * **Se mudo a `lib/tiposDeVinculo.js` el 2026-10-08**, cuando el tablero paso a dejar elegir el
 * tipo: el selector necesita las mismas cuatro siglas que la flecha rotula, y dos tablas con las
 * mismas siglas se separan en silencio.
 */
const PUNTAS = TIPOS_DE_VINCULO;
const ALTO_CABECERA = 38;
/** La cabecera de la franja de carga. Va en las dos columnas o las filas quedan corridas. */
const ALTO_CABECERA_CARGA = 20;
/** Filas de mas arriba y abajo de la ventana visible, para que el scroll no muestre huecos. */
const COLCHON = 6;

function Cabecera({ eje }) {
  return (
    <div className="relative border-b border-brand-border bg-white" style={{ height: ALTO_CABECERA, width: eje.ancho }}>
      {eje.meses.map((m) => (
        <div
          key={m.key}
          className="absolute top-0 h-[17px] border-r border-zinc-100 text-[9px] font-semibold uppercase tracking-[0.08em] text-zinc-500 flex items-center px-1 overflow-hidden"
          style={{ left: m.x, width: m.w }}
        >
          {m.label}
        </div>
      ))}
      {eje.marcas.map((t) => (
        <div
          key={t.key}
          className={`absolute top-[17px] h-[20px] flex flex-col items-center justify-center leading-none ${t.finde ? 'text-zinc-300' : 'text-zinc-500'}`}
          style={{ left: t.x, width: t.w }}
        >
          <span className="text-[8px]">{t.bajo}</span>
          <span className="text-[9px] font-semibold">{t.alto}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * El fondo compartido: fines de semana, lineas de semana y el "hoy". Lo dibujan las dos vistas.
 *
 * **La linea de hoy esta rotulada desde el 2026-10-08**, y con el Gantt abriendo posicionado en
 * ella eso importa mas, no menos: era 1 px al 60 % de opacidad, indistinguible de una linea de
 * grilla semanal — en un rango de diez años, que es el que tiene esta capsula, no habia forma de
 * saber cual era. El rotulo va **solo en el panel del plan** (`rotular`), porque en la franja de
 * carga de abajo seria la misma informacion dos veces sobre el mismo eje.
 */
function Fondo({ eje, alto, hoyX, rotular = false }) {
  return (
    <>
      {eje.findes.map((w) => (
        <div key={w.key} className="absolute top-0" style={{ left: w.x, width: w.w, height: alto, background: COLOR.finde }} />
      ))}
      {eje.dias.filter((d) => d.d.getDay() === 1).map((d) => (
        <div key={`g${d.i}`} className="absolute top-0 w-px" style={{ left: d.x, height: alto, background: COLOR.grilla }} />
      ))}
      {hoyX !== null && (
        <>
          <div className="absolute top-0 w-px" style={{ left: hoyX, height: alto, background: COLOR.marca }} />
          {rotular && (
            <div
              className="absolute top-0 text-[8px] uppercase tracking-wider px-1 py-px rounded-sm text-white whitespace-nowrap"
              style={{ left: hoyX + 2, background: COLOR.marca }}
              data-testid="gantt-rotulo-hoy"
            >
              hoy {aIso(new Date()).slice(5)}
            </div>
          )}
        </>
      )}
    </>
  );
}

function Barra({
  row, layout, fantasma, propuesta, arrastrando, deltaPx, modoArrastre,
  vinculando, puedeArrastrar, pctEnVivo, activa = false,
  onHover, onSalir, onClick, onIniciarArrastre, onArmarVinculo, onCompletarVinculo,
}) {
  const deducida = fechaDeducida(row) && !propuesta;
  // **Avance y consumo son dos cosas, y hasta el 2026-10-05 esta barra dibujaba una sola.**
  // `progress` era `consumed/estimate`, o sea el consumo, pintado como si fuera lo hecho: una tarea
  // que insumio el doble se veia al 100 % sin estar hecha, y una terminada sin horas imputadas al
  // 0 %. Ahora `progress` es lo hecho —declarado por quien trabaja, o 100 si el estado es terminal—
  // y `consumo` es lo gastado. `null` en el avance significa **nadie lo declaro**, y entonces no se
  // pinta relleno: un relleno en cero afirma que no se avanzo.
  const avance = row.progress == null ? null : Math.round(Number(row.progress) * 100);
  const consumo = row.consumo == null ? null : Math.round(Number(row.consumo) * 100);
  const seFueDeMano = Boolean(row.consumio_mas_de_lo_que_avanzo);
  const esTarea = row.row_type === 'task';
  const dx = arrastrando && modoArrastre === MODO.MOVER ? deltaPx : 0;
  const dw = arrastrando && modoArrastre === MODO.ESTIRAR ? deltaPx : 0;

  if (!esTarea) {
    // Ticket y fase son rollups, no trabajo: se dibujan como corchete y no como barra llena,
    // para que no compitan visualmente con las tareas —que son las unicas que se planifican—.
    const color = row.row_type === 'ticket' ? COLOR.ticket : COLOR.fase;
    const grosor = row.row_type === 'ticket' ? 6 : 4;
    return (
      <button
        type="button"
        className="absolute"
        style={{ left: layout.left, width: layout.width, top: (ALTO_DE_FILA - grosor) / 2, height: grosor, background: color }}
        aria-label={row.label}
        onMouseEnter={(e) => onHover(row, e.currentTarget.getBoundingClientRect())}
        onFocus={(e) => onHover(row, e.currentTarget.getBoundingClientRect())}
        onMouseLeave={onSalir}
        onBlur={onSalir}
        onClick={() => onClick?.(row)}
        data-testid={`gantt-bar-${row.id}`}
      />
    );
  }

  return (
    <>
      {/* De donde venia. Se dibuja solo cuando hay algo propuesto: sin la referencia, el diff
          dice "+3 d" y la pantalla no muestra respecto de que. */}
      {fantasma && (
        <div
          className="absolute rounded-sm pointer-events-none"
          style={{
            left: fantasma.left, width: fantasma.width, top: (ALTO_DE_FILA - 14) / 2, height: 14,
            border: '1px dashed #A1A1AA', opacity: 0.7,
          }}
        />
      )}
      <button
        type="button"
        className={`absolute rounded-sm overflow-visible text-left ${
          vinculando && vinculando !== row.task_id
            ? 'cursor-crosshair'
            : puedeArrastrar ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        style={{
          left: layout.left + dx,
          width: Math.max(4, layout.width + dw),
          top: (ALTO_DE_FILA - 14) / 2,
          height: 14,
          background: deducida
            ? 'repeating-linear-gradient(90deg,#EDEFF7 0 4px,#F7F8FC 4px 8px)'
            : COLOR.pista,
          border: propuesta
            ? `1px solid ${COLOR.aviso}`
            : deducida ? `1px dashed ${COLOR.inferido}` : '1px solid transparent',
          // Las candidatas se marcan mientras se elige destino. Decir "toca la tarea que
          // espera a esta" sin senalar cuales son deja el trabajo de adivinar cual acepta el
          // toque — que es la mitad de por que el gesto se sentia roto.
          // El orden importa: arrastrar manda sobre todo, despues «sos candidata a vincularse»,
          // y al final la fila activa. Si la fila activa ganara, taparia la señal del gesto en
          // curso — que es la que la persona esta mirando en ese momento.
          boxShadow: arrastrando
            ? '0 1px 6px rgba(0,0,0,.18)'
            : vinculando && vinculando !== row.task_id
              ? `0 0 0 1.5px ${COLOR.marca}`
              : activa ? `0 0 0 1.5px ${COLOR.marca}, 0 1px 4px rgba(0,47,167,.25)` : undefined,
          zIndex: arrastrando ? 5 : undefined,
        }}
        aria-label={row.label}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          // Si hay un vinculo armado, esta barra es el destino y **no** arranca un arrastre.
          if (vinculando && vinculando !== row.task_id) {
            e.preventDefault();
            e.stopPropagation();
            onCompletarVinculo?.(row);
            return;
          }
          if (!puedeArrastrar) return;
          onIniciarArrastre?.(row, MODO.MOVER, e);
        }}
        onMouseEnter={(e) => onHover(row, e.currentTarget.getBoundingClientRect())}
        onFocus={(e) => onHover(row, e.currentTarget.getBoundingClientRect())}
        onMouseLeave={onSalir}
        onBlur={onSalir}
        onClick={() => { if (!puedeArrastrar) onClick?.(row); }}
        data-testid={`gantt-bar-${row.id}`}
      >
        {/* Lo hecho. Sin declarar no se pinta nada: la barra vacia dice «no se sabe». */}
        {avance != null && (
          <span
            className="absolute left-0 top-0 bottom-0 rounded-sm pointer-events-none"
            style={{ width: `${avance}%`, background: COLOR.marca, opacity: deducida ? 0.55 : 1 }}
          />
        )}
        {/* Lo gastado, como una linea fina al pie y no como relleno: no compite con el avance, y
            la comparacion entre las dos es la informacion. Roja cuando se gasto mas de lo que se
            avanzo, que es la alerta que llega a tiempo. */}
        {consumo != null && (
          <span
            className="absolute left-0 bottom-0 pointer-events-none"
            style={{
              width: `${Math.min(100, consumo)}%`,
              height: 3,
              background: seFueDeMano ? COLOR.peligro : '#A1A1AA',
              opacity: 0.95,
            }}
          />
        )}
        {/* Mientras se estira, el numero **se mueve**: estirar cambia la dedicacion, y verla
            recien al soltar obliga a tirar a ciegas y mirar despues si quedo donde uno queria. */}
        {(pctEnVivo != null || (row.asignacion_pct != null && row.asignacion_pct < 100))
          && layout.width > 30 && (
          <span
            className="absolute right-1 top-0 bottom-0 flex items-center text-[8px] font-mono pointer-events-none"
            style={{ color: pctEnVivo != null ? '#8A6400' : COLOR.marca, fontWeight: pctEnVivo != null ? 700 : 400 }}
          >
            {pctEnVivo ?? row.asignacion_pct}%
          </span>
        )}
        {puedeArrastrar && !(vinculando && vinculando !== row.task_id) && (
          <>
            {/*
              El borde estira. Solo si hay estimacion: sin horas no hay nada que repartir entre
              mas dias, y una manija que no hace nada es peor que ninguna.

              **No puede solaparse con el punto de vincular.** Median 8 px pegados al borde
              (R-4 a R+4) contra 20 px del punto (R-4 a R+16): se pisaban exactamente, y como
              el punto se dibuja despues quedaba encima y se llevaba todos los clics. Estirar
              era inalcanzable aunque la manija estuviera ahi. Ahora la de estirar termina en
              R+3 y la de vincular arranca en R+4.

              Y se ve: era una linea blanca sobre una pista celeste clara —invisible— asi que
              ademas de inalcanzable no habia forma de saber que existia.
            */}
            {Number(row.estimate_hours) > 0 && (
              <span
                className="absolute cursor-ew-resize opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                style={{ right: -3, width: 10, top: -3, bottom: -3 }}
                title={`Estirar cambia la dedicación (hoy ${row.asignacion_pct ?? 100} %), no las ${row.estimate_hours} h estimadas`}
                onPointerDown={(e) => onIniciarArrastre?.(row, MODO.ESTIRAR, e)}
                data-testid={`gantt-estirar-${row.id}`}
              >
                <span
                  className="absolute rounded-sm"
                  style={{ right: 3, top: 2, bottom: 2, width: 3, background: COLOR.marca }}
                />
              </span>
            )}
            {/*
              El extremo vincula.

              Se arma en **pointerdown** y no en un `onClick` posterior. La primera version
              hacia `preventDefault()` en el pointerdown y esperaba el click: preventDefault
              sobre pointerdown suprime los eventos de mouse de compatibilidad, y si el click
              llega o no depende del navegador. Resultado: se hacia clic en el punto y no
              pasaba nada, sin ningun error que mirar.

              Y el area es grande a proposito. Antes eran 8 px a 14 px **afuera** de la barra:
              acercarse implicaba salir de la barra, con lo cual el `group-hover` se apagaba y
              el punto desaparecia justo cuando lo ibas a tocar.
            */}
            <span
              className={`absolute flex items-center justify-center cursor-crosshair ${
                vinculando === row.task_id
                  ? 'opacity-100'
                  // Con un vinculo armado, las manijas de las demas tareas se apagan: ahi ya
                  // no estas eligiendo desde donde vincular sino **hacia donde**, y una manija
                  // viva sobre la barra destino te re-arma desde ella en vez de completar. Era
                  // justo lo que pasaba: el segundo toque no caia en la barra, caia en otra
                  // manija, y la franja cambiaba de origen en vez de cerrar el vinculo.
                  : vinculando
                    ? 'opacity-0 pointer-events-none'
                    // **`opacity-0` no deja de recibir clics.** Cada tarea tenia una trampa
                    // invisible de 20 px flotando a la derecha de su barra, encima de la
                    // barra siguiente cuando estaban pegadas.
                    : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'
              }`}
              style={{ right: -22, width: 18, top: -2, height: 18 }}
              title={vinculando === row.task_id
                ? 'Elegí ahora la tarea que espera a esta. Esc cancela.'
                : 'Vincular: tocá acá y después la tarea que espera a esta'}
              onPointerDown={(e) => { e.stopPropagation(); onArmarVinculo?.(row); }}
              data-testid={`gantt-vincular-${row.id}`}
            >
              <span
                className={`w-2.5 h-2.5 rounded-full border ${
                  vinculando === row.task_id
                    ? 'bg-brand-primary border-brand-primary'
                    : 'bg-white border-zinc-400'
                }`}
              />
            </span>
          </>
        )}
      </button>
    </>
  );
}

export default function GanttChart({
  rows,
  dependencies,
  meta,
  carga,
  pxPorDia,
  onZoom,
  onRowClick,
  showAssignees = true,
  verCarga = true,
  puedeArrastrar = true,
  propuesta = null,
  onCambioDePlan,
  onNuevoVinculo,
  onQuitarVinculo,
  // Un ref donde este componente deja su `irAHoy` para que la toolbar lo pueda llamar. El scroll
  // es de aca: mudarlo afuera obligaria a compartir los dos refs de los dos paneles.
  apiRef = null,
  // Cuantas filas tienen su barra fuera del rango que se esta mirando. Lo calcula este
  // componente y lo informa para arriba, porque es quien sabe donde quedo cada barra.
  onFueraDeRango,
}) {
  const planRef = useRef(null);
  const cargaRef = useRef(null);
  const sincronizando = useRef(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [altoVisible, setAltoVisible] = useState(480);
  const [encima, setEncima] = useState(null);
  const [vinculando, setVinculando] = useState(null);
  // ── la fila activa: lo que une las dos columnas ──────────────────────────────────────
  //
  // **Diego lo pidió asi: «si en la lista WBS me paro en una tarea, debería demarcarse la fila
  // del GANTT para entender que es efectivamente la tarea».** Hasta hoy no habia **ningun**
  // estado de seleccion: en la WBS el nombre era un `<Link>` que saca de la pantalla, y el
  // `hover:bg-zinc-50` pintaba solo la celda de la izquierda. Con 97 filas y un eje de diez
  // años, saber cual barra es cual se hacia contando renglones con el dedo.
  //
  // `fijada` existe porque el hover no alcanza: para mirar una barra hay que mover el mouse
  // hacia ella, y en el camino el hover se va. Un clic en la fila la deja marcada.
  const [filaEncima, setFilaEncima] = useState(null);
  const [filaFijada, setFilaFijada] = useState(null);
  const filaActiva = filaFijada ?? filaEncima;
  // El gesto se resuelve en un handler que no se vuelve a crear en cada render, asi que lee
  // el estado por ref: si dependiera del closure, tocar una barra usaria el valor de antes.
  const vinculandoRef = useRef(null);

  const eje = useMemo(
    () => construirEje({
      desde: meta?.range_start, hasta: meta?.range_end, pxPorDia, horario: meta?.horario,
    }),
    [meta?.range_start, meta?.range_end, pxPorDia, meta?.horario],
  );
  const hoyX = useMemo(() => eje.xDe(aIso(new Date())), [eje]);
  const alto = (rows?.length || 0) * ALTO_DE_FILA;

  // Cuando hay una simulacion pendiente, las barras se dibujan **donde quedarian**, y la
  // posicion vieja queda como fantasma punteado. Un diff que dice "+3 d" sin mostrar respecto
  // de que obliga a imaginarse el movimiento.
  const layouts = useMemo(
    () => (rows || []).map((r) => {
      const prop = propuesta?.[r.task_id];
      return eje.barra(prop?.planned_start ?? r.start, prop?.planned_end ?? r.end);
    }),
    [rows, eje, propuesta],
  );
  const fantasmas = useMemo(
    () => (rows || []).map((r) => (propuesta?.[r.task_id] ? eje.barra(r.start, r.end) : null)),
    [rows, eje, propuesta],
  );

  // ── las barras que quedaron fuera del rango ─────────────────────────────────────────
  //
  // `eje.barra` devuelve `null` cuando la barra cae entera afuera de lo que se esta mirando, y
  // hasta hoy eso dibujaba **una fila sin nada y sin explicacion**. No era visible porque el
  // rango arrancaba en la fecha mas vieja que hubiera, asi que nunca pasaba; con una ventana que
  // se puede acotar, pasa — y una fila vacia se lee como «esta tarea no tiene fechas», que es
  // otra cosa completamente distinta.
  //
  // Se dice de **que lado** quedo, porque es la mitad util: «antes» se alcanza ensanchando hacia
  // atras y «despues» hacia adelante.
  const fueraDeRango = useMemo(() => (rows || []).map((r, i) => {
    if (layouts[i] || !r.start) return null;
    return r.end && r.end < String(meta?.range_start || '') ? 'antes' : 'despues';
  }), [rows, layouts, meta?.range_start]);

  const cuantasFuera = useMemo(() => fueraDeRango.filter(Boolean).length, [fueraDeRango]);
  useEffect(() => { onFueraDeRango?.(cuantasFuera); }, [cuantasFuera, onFueraDeRango]);

  const alSoltar = useCallback((row, modo, deltaDias) => {
    const cambio = cambioDelGesto({
      row, modo, deltaDias,
      horario: meta?.horario,
      horasPorDia: meta?.horas_por_dia,
    });
    if (cambio) onCambioDePlan?.(cambio);
  }, [meta?.horario, meta?.horas_por_dia, onCambioDePlan]);

  const alToque = useCallback((row, modo) => {
    // El vinculo ya no se completa aca: se resuelve en el `pointerdown` de la barra destino,
    // que es determinista. Lo que queda es lo de siempre — tocar una barra abre su tarea.
    if (modo === MODO.MOVER) onRowClick?.(row);
  }, [onRowClick]);

  const { arrastre, iniciar } = useArrastre({ px: eje.px, onSoltar: alSoltar, onToque: alToque });

  /**
   * La dedicacion que resultaria del estirado en curso.
   *
   * Se calcula con la misma cuenta que va a aplicar el backend, no con una aproximacion: si
   * el numero que se ve mientras se arrastra no fuera el que se guarda, el gesto seria una
   * promesa que el diff desmiente.
   */
  const dedicacionEnVivo = useMemo(() => {
    if (arrastre?.modo !== MODO.ESTIRAR) return null;
    const fila = (rows || []).find((r) => r.id === arrastre.id);
    if (!fila || !(Number(fila.estimate_hours) > 0)) return null;
    const ini = aFecha(fila.start);
    const fin = aFecha(fila.end);
    if (!ini || !fin) return null;
    const dias = diasHabilesEntre(ini, sumarDias(fin, arrastre.deltaDias || 0), meta?.horario);
    if (!dias) return null;
    return dedicacionParaDurar({
      estimateHours: fila.estimate_hours, horasPorDia: meta?.horas_por_dia, dias,
    });
  }, [arrastre, rows, meta?.horario, meta?.horas_por_dia]);

  useEffect(() => { vinculandoRef.current = vinculando; }, [vinculando]);

  /** Tocar el punto arma; tocarlo otra vez cancela. Sin salida, un modo es una trampa. */
  const armarVinculo = useCallback(
    (row) => setVinculando((actual) => (actual === row.task_id ? null : row.task_id)),
    [],
  );

  const completarVinculo = useCallback((row) => {
    const desde = vinculandoRef.current;
    setVinculando(null);
    if (desde && desde !== row.task_id) onNuevoVinculo?.(desde, row.task_id);
  }, [onNuevoVinculo]);

  // Escapar de un modo tiene que ser una tecla, no adivinar donde hacer clic.
  useEffect(() => {
    if (!vinculando) return undefined;
    const alaTecla = (e) => { if (e.key === 'Escape') setVinculando(null); };
    document.addEventListener('keydown', alaTecla);
    return () => document.removeEventListener('keydown', alaTecla);
  }, [vinculando]);

  /** El indice de cada fila, para colgar las flechas y las verticales de compromiso. */
  const indicePorId = useMemo(() => {
    const m = {};
    (rows || []).forEach((r, i) => { m[r.id] = i; });
    return m;
  }, [rows]);

  /**
   * Una vertical por ticket, **sobre sus propias filas y no sobre todo el tablero**. Una linea
   * de punta a punta obligaria a seguirla con el dedo para saber de que caso es.
   */
  const compromisos = useMemo(() => (rows || [])
    .filter((r) => r.row_type === 'ticket' && r.compromiso?.compromiso)
    .map((r) => {
      const x = eje.xFinDe(r.compromiso.compromiso);
      if (x === null) return null;
      const idx = indicePorId[r.id];
      const suyas = (rows || []).filter((o) => o.ticket_id === r.ticket_id).map((o) => indicePorId[o.id]);
      const desde = Math.min(idx, ...suyas);
      const hasta = Math.max(idx, ...suyas);
      return {
        key: r.id, x,
        top: desde * ALTO_DE_FILA,
        alto: (hasta - desde + 1) * ALTO_DE_FILA,
        cruzado: r.compromiso.estado === 'no_entra',
      };
    })
    .filter(Boolean), [rows, eje, indicePorId]);

  // ── ventana visible ─────────────────────────────────────────────────────────────────
  // 2000 filas son 2000 nodos por lane. Se dibuja solo lo que se ve mas un colchon: la altura
  // total la sostiene un espaciador, asi que la barra de scroll sigue diciendo la verdad.
  const desdeFila = Math.max(0, Math.floor(scrollTop / ALTO_DE_FILA) - COLCHON);
  const hastaFila = Math.min(
    (rows?.length || 0),
    Math.ceil((scrollTop + altoVisible) / ALTO_DE_FILA) + COLCHON,
  );
  const ventana = useMemo(
    () => (rows || []).slice(desdeFila, hastaFila).map((r, k) => ({ row: r, i: desdeFila + k })),
    [rows, desdeFila, hastaFila],
  );

  // ── las flechas ─────────────────────────────────────────────────────────────────────
  //
  // **De que punta sale y a que punta llega lo dice el tipo**, y hasta el 2026-10-08 las cuatro
  // se dibujaban igual: siempre de fin-de-A a inicio-de-B. Los cuatro tipos y el `lag_dias`
  // viajan en la respuesta desde que existen, y el grafico los ignoraba — asi que una
  // dependencia «comienzo a comienzo» se veia como una «fin a comienzo», que dice otra cosa.
  const flechas = useMemo(() => (dependencies || []).map((dep) => {
    const a = indicePorId[dep.from_id];
    const b = indicePorId[dep.to_id];
    if (a === undefined || b === undefined) return null;
    if (Math.max(a, b) < desdeFila || Math.min(a, b) > hastaFila) return null;
    const la = layouts[a];
    const lb = layouts[b];
    if (!la || !lb) return null;
    const tipo = PUNTAS[dep.type] || PUNTAS.finish_to_start;
    const x1 = tipo.de === 'fin' ? la.left + la.width : la.left;
    const x2 = tipo.a === 'inicio' ? lb.left : lb.left + lb.width;
    const y1 = a * ALTO_DE_FILA + ALTO_DE_FILA / 2;
    const y2 = b * ALTO_DE_FILA + ALTO_DE_FILA / 2;
    const codo = Math.max(x1 + 6, x2 - 6);
    const lag = Number(dep.lag_dias) || 0;
    return {
      key: dep.link_id || `${dep.from_id}-${dep.to_id}`,
      linkId: dep.link_id,
      capsuleId: dep.capsule_id,
      d: `M ${x1} ${y1} H ${codo} V ${y2} H ${x2}`,
      // Solo la de fin a comienzo va llena: es la que la gente da por supuesta, asi que las
      // otras tres tienen que **verse** distintas o se leen como ella.
      punteada: dep.type && dep.type !== 'finish_to_start',
      sigla: tipo.sigla,
      lag,
      // El rotulo va en el codo, que es donde la flecha tiene lugar propio.
      lx: codo,
      ly: (y1 + y2) / 2,
      titulo: `${tipo.sigla}: ${tipo.de} de la predecesora → ${tipo.a} de esta`
        + (lag ? ` · ${lag > 0 ? `espera ${lag} d` : `solapa ${-lag} d`}` : '')
        + (dep.link_id ? ' · clic para quitar' : ''),
    };
  }).filter(Boolean), [dependencies, indicePorId, layouts, desdeFila, hastaFila]);

  // **Las flechas que no se pueden dibujar se cuentan, no se descartan en silencio.** Una punta
  // fuera de la ventana virtual o fuera del rango hacia que la flecha no saliera y **nada lo
  // dijera**: la tarea se ve libre estando bloqueada, que es la peor forma de equivocarse en un
  // plan. Es el mismo defecto que una barra fuera de rango, un nivel mas arriba.
  const flechasOcultas = useMemo(() => {
    const porFila = (rows || []).map(() => 0);
    (dependencies || []).forEach((dep) => {
      const a = indicePorId[dep.from_id];
      const b = indicePorId[dep.to_id];
      if (a === undefined || b === undefined) return;
      const fueraDeLaVentana = Math.max(a, b) < desdeFila || Math.min(a, b) > hastaFila;
      const sinBarra = !layouts[a] || !layouts[b];
      if (fueraDeLaVentana || sinBarra) porFila[b] += 1;
    });
    return porFila;
  }, [rows, dependencies, indicePorId, layouts, desdeFila, hastaFila]);

  // ── scroll compartido ───────────────────────────────────────────────────────────────
  // Las dos vistas miran el mismo eje: si una se corre y la otra no, la pantalla entera deja
  // de decir la verdad. Se sincroniza el scroll horizontal en los dos sentidos, con un flag
  // para que un `scrollLeft` programatico no dispare el del otro y se retroalimenten.
  const sincronizar = useCallback((origen, destino) => {
    if (sincronizando.current) return;
    sincronizando.current = true;
    if (destino.current) destino.current.scrollLeft = origen.current.scrollLeft;
    requestAnimationFrame(() => { sincronizando.current = false; });
  }, []);

  const alScrollearPlan = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
    setEncima(null);
    sincronizar(planRef, cargaRef);
  }, [sincronizar]);

  useLayoutEffect(() => {
    const el = planRef.current;
    if (!el) return undefined;
    const medir = () => setAltoVisible(el.clientHeight || 480);
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    if (ro) ro.observe(el);
    return () => { if (ro) ro.disconnect(); };
  }, []);

  // ── posicionarse en hoy ─────────────────────────────────────────────────────────────
  //
  // **El Gantt abria en el extremo izquierdo del rango, que es la fecha mas vieja que hay.**
  // En esta capsula eso es 2016: habia que scrollear diez años para llegar a esta semana. Y
  // `hoyX` ya estaba calculado desde siempre — se usaba para pintar una linea de 1 px y nada
  // mas. La pieza estaba; lo que faltaba era usarla.
  //
  // Se deja un cuarto del ancho visible **antes** de hoy, no se centra: lo que esta por venir
  // es lo que se mira, y lo de atras alcanza como contexto de lo que viene llegando.
  const irAHoy = useCallback(() => {
    const el = planRef.current;
    if (!el || hoyX === null) return;
    el.scrollLeft = Math.max(0, hoyX - (el.clientWidth - ANCHO_NOMBRES) * 0.33);
    sincronizar(planRef, cargaRef);
  }, [hoyX, sincronizar]);

  // **Solo al cambiar el rango, no en cada render.** Si dependiera del zoom, cada rueda del
  // mouse devolveria la vista a hoy y seria imposible mirar otra fecha; y re-posicionar
  // mientras alguien scrollea es pelearle la pantalla.
  const rangoPosicionado = useRef(null);
  useLayoutEffect(() => {
    const clave = `${meta?.range_start}|${meta?.range_end}`;
    if (!rows?.length || rangoPosicionado.current === clave) return;
    rangoPosicionado.current = clave;
    irAHoy();
  }, [meta?.range_start, meta?.range_end, rows?.length, irAHoy]);

  // ── zoom desde la toolbar: tambien ancla ────────────────────────────────────────────
  //
  // **Los botones de escala y el slider no anclaban, y la rueda con Ctrl si.** Medido en el
  // navegador el 2026-10-08: pasar de «mes» a «semana» triplica el lienzo —14.689 a 43.425 px—
  // y el navegador **deja `scrollLeft` donde estaba**, asi que la fecha que se estaba mirando se
  // va a 29.555 px: fuera de pantalla, a la derecha. Diego lo reporto como *«lo que hay
  // planificado hoy se me va al final del grafico»*, y es exactamente eso.
  //
  // **Se ancla en hoy cuando hoy se esta viendo, y en el centro cuando no.** Anclar siempre en
  // el centro era correcto en la aritmetica y equivocado en la practica: medido, con el rango de
  // esta capsula el centro cae meses antes de hoy —porque el eje termina ocho dias despues de
  // hoy y la vista queda pegada al extremo derecho—, asi que al pasar de «mes» a «semana» hoy se
  // iba igual de pantalla, solo mas cerca: de 29.555 px a 1.555, con 1.058 de ancho visible.
  //
  // La regla que queda es la que una persona espera: *el zoom conserva lo que estas mirando, y si
  // estas mirando hoy, conserva hoy*. Scrollear a marzo y hacer zoom sigue conservando marzo.
  const zoomAnclandoElCentro = useCallback((px) => {
    const el = planRef.current;
    if (!el) { onZoom?.(px); return; }
    const anchoDelEje = Math.max(1, el.clientWidth - ANCHO_NOMBRES);
    const hoyEnPantalla = hoyX === null ? null : hoyX - el.scrollLeft;
    const hoySeEstaViendo = hoyEnPantalla !== null
      && hoyEnPantalla >= 0 && hoyEnPantalla <= anchoDelEje;
    const { pxPorDia: nuevo, scrollLeft } = zoomAnclado({
      pxPorDia,
      factor: px / pxPorDia,
      xPuntero: hoySeEstaViendo ? hoyEnPantalla : anchoDelEje / 2,
      scrollLeft: el.scrollLeft,
    });
    onZoom?.(nuevo);
    requestAnimationFrame(() => {
      if (planRef.current) {
        planRef.current.scrollLeft = Math.max(0, scrollLeft);
        sincronizar(planRef, cargaRef);
      }
    });
  }, [pxPorDia, hoyX, onZoom, sincronizar]);

  // El boton «Hoy» y el zoom viven en la toolbar, que es hermana de este componente: se les pasa
  // la funcion por un ref en vez de mudar el scroll afuera, porque el scroll es de aca.
  useEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = { irAHoy, zoomAnclandoElCentro };
    return () => { apiRef.current = null; };
  }, [apiRef, irAHoy, zoomAnclandoElCentro]);

  // ── zoom anclado en el puntero ──────────────────────────────────────────────────────
  // `passive: false` porque hay que cancelar el scroll del navegador. React no deja declararlo,
  // asi que el listener va a mano.
  useEffect(() => {
    const el = planRef.current;
    if (!el || !onZoom) return undefined;
    const alRodar = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const caja = el.getBoundingClientRect();
      // `scrollLeft` del contenedor incluye la columna de nombres; el eje empieza despues.
      // Se compensa **una sola vez**, en el puntero: restarlo tambien del scroll anclaria el
      // zoom 320 px a la izquierda de donde esta el mouse, que es peor que no anclarlo.
      const xEnElEje = Math.max(0, e.clientX - caja.left - ANCHO_NOMBRES);
      const { pxPorDia: nuevo, scrollLeft } = zoomAnclado({
        pxPorDia,
        factor: e.deltaY < 0 ? 1.12 : 1 / 1.12,
        xPuntero: xEnElEje,
        scrollLeft: el.scrollLeft,
      });
      onZoom(nuevo);
      requestAnimationFrame(() => {
        if (planRef.current) planRef.current.scrollLeft = Math.max(0, scrollLeft);
      });
    };
    el.addEventListener('wheel', alRodar, { passive: false });
    return () => el.removeEventListener('wheel', alRodar);
  }, [pxPorDia, onZoom]);

  const alPasar = useCallback((row, rect) => setEncima({ row, rect }), []);
  const alSalir = useCallback(() => setEncima(null), []);

  if (!rows?.length) {
    return (
      <div className="border border-brand-border rounded-sm p-8 text-center text-xs text-zinc-500" data-testid="gantt-empty">
        Sin filas en el rango seleccionado. Crea tareas con fechas planificadas o ajusta filtros.
      </div>
    );
  }

  const personas = carga?.personas || [];
  const tareaOrigen = vinculando
    ? (rows || []).find((r) => r.task_id === vinculando)
    : null;

  return (
    <div className="border border-brand-border rounded-sm bg-white flex flex-col overflow-hidden" data-testid="gantt-chart">
      {/*
        Que el modo esta armado tiene que verse. Sin esto, tocar el punto y que no pase nada
        visible es indistinguible de que el clic no haya funcionado — y era exactamente lo que
        pasaba: no habia forma de saber si el primer paso del gesto habia quedado hecho.
      */}
      {tareaOrigen && (
        <div
          className="px-3 py-1.5 bg-brand-primary/5 border-b border-brand-border flex items-center gap-2 text-[11px]"
          data-testid="gantt-vinculando"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-brand-primary shrink-0" />
          <span>
            Vinculando desde <b>{tareaOrigen.label}</b>. Tocá la tarea que espera a ésta.
          </span>
          <button
            type="button"
            className="ml-auto text-[10px] text-zinc-500 hover:text-[#09090B] underline"
            onClick={() => setVinculando(null)}
          >
            Cancelar (Esc)
          </button>
        </div>
      )}

      {/* ── plan ── */}
      <div
        ref={planRef}
        className="overflow-auto"
        style={{ maxHeight: '58vh' }}
        onScroll={alScrollearPlan}
      >
        <div className="flex min-w-max">
          <div
            className="shrink-0 sticky left-0 z-20 bg-white border-r border-brand-border"
            style={{ width: ANCHO_NOMBRES }}
          >
            <div className="sticky top-0 z-10 bg-zinc-50 border-b border-brand-border px-2 flex items-center text-[9px] font-semibold uppercase tracking-[0.1em] text-zinc-500"
              style={{ height: ALTO_CABECERA }}>
              WBS · Nombre{showAssignees ? ' · Responsable' : ''}
            </div>
            <div className="relative" style={{ height: alto }}>
              {ventana.map(({ row, i }) => {
                const who = row.row_type === 'task' ? assigneeLabel(row.assignee) : '';
                const marca = marcaDeCompromiso(row);
                const nivel = (row.wbs?.split('.').length || 1) - 1;
                return (
                  <div
                    key={row.id}
                    className={`absolute left-0 right-0 flex items-center gap-1.5 pr-2 text-[10px] border-b border-zinc-50 cursor-pointer ${
                      filaActiva === row.id ? 'bg-[#EEF2FF]' : 'hover:bg-zinc-50'}`}
                    style={{ top: i * ALTO_DE_FILA, height: ALTO_DE_FILA, paddingLeft: 10 + nivel * 10 }}
                    onMouseEnter={() => setFilaEncima(row.id)}
                    onMouseLeave={() => setFilaEncima((v) => (v === row.id ? null : v))}
                    // Clic en la fila **fija** la marca; el nombre sigue siendo un link que
                    // abre el caso, y por eso el link detiene la propagacion mas abajo.
                    onClick={() => setFilaFijada((v) => (v === row.id ? null : row.id))}
                    data-testid={`gantt-wbs-${row.id}`}
                  >
                    <span
                      className="absolute left-0 top-1 bottom-1 w-[3px]"
                      style={{ background: row.row_type === 'ticket' ? colorDePrioridad(row.priority) : 'transparent' }}
                    />
                    <span className="text-zinc-400 font-mono text-[9px] shrink-0">{row.wbs}</span>
                    {row.open_url ? (
                      <Link
                        to={row.open_url}
                        onClick={(e) => e.stopPropagation()}
                        className={`truncate hover:text-brand-primary ${row.row_type === 'ticket' ? 'font-semibold' : ''}`}
                      >
                        {row.label}
                      </Link>
                    ) : (
                      <span className="truncate">{row.label}</span>
                    )}
                    {showAssignees && who && (
                      <span className="text-zinc-400 truncate text-[9px] font-mono shrink" data-testid={`gantt-assignee-${row.id}`}>
                        · {who}
                      </span>
                    )}
                    {flechasOcultas[i] > 0 && (
                      <span
                        className="ml-auto shrink-0 text-[8px] uppercase tracking-wider px-1 py-px border border-amber-300 bg-[#FFFCF2] text-[#8A6400] rounded-sm font-mono"
                        title={`${flechasOcultas[i]} dependencia(s) de esta tarea no se pueden dibujar: la predecesora quedo fuera de lo que estas viendo. La tarea esta atada aunque no veas la flecha.`}
                        data-testid={`gantt-flechas-ocultas-${row.id}`}
                      >
                        ⇠{flechasOcultas[i]}
                      </span>
                    )}
                    {fueraDeRango[i] && (
                      <span
                        className={`${flechasOcultas[i] > 0 ? '' : 'ml-auto'} shrink-0 text-[8px] uppercase tracking-wider px-1 py-px border border-amber-300 bg-[#FFFCF2] text-[#8A6400] rounded-sm`}
                        title={`Su barra queda ${fueraDeRango[i]} del rango que estas mirando. Ensanchalo para verla.`}
                        data-testid={`gantt-fuera-de-rango-${row.id}`}
                      >
                        {fueraDeRango[i] === 'antes' ? '◂ antes' : 'después ▸'}
                      </span>
                    )}
                    {marca && (
                      <span
                        className={`${fueraDeRango[i] || flechasOcultas[i] > 0 ? '' : 'ml-auto'} shrink-0 text-[8px] uppercase tracking-wider px-1 py-px border rounded-sm ${marca.clase}`}
                        data-testid={`gantt-compromiso-${row.id}`}
                      >
                        {marca.texto}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative" style={{ width: eje.ancho }}>
            <div className="sticky top-0 z-10">
              <Cabecera eje={eje} />
            </div>
            <div className="relative" style={{ height: alto }}>
              <Fondo eje={eje} alto={alto} hoyX={hoyX} rotular />
              {compromisos.map((c) => (
                <div
                  key={c.key}
                  className="absolute"
                  style={{
                    left: c.x, top: c.top, height: c.alto, width: 0,
                    borderLeft: `1.5px dashed ${c.cruzado ? COLOR.peligro : '#09090B'}`,
                  }}
                  data-testid={`gantt-linea-compromiso-${c.key}`}
                />
              ))}
              {ventana.map(({ row, i }) => {
                const layout = layouts[indicePorId[row.id]];
                if (!layout) return null;
                return (
                  <div
                    key={row.id}
                    className={`absolute left-0 right-0 group ${filaActiva === row.id ? 'bg-[#EEF2FF]' : ''}`}
                    style={{ top: i * ALTO_DE_FILA, height: ALTO_DE_FILA }}
                    // El resaltado va en los dos sentidos: pasar por la barra marca su fila de la
                    // WBS, que es la mitad que falta para no contar renglones con el dedo.
                    onMouseEnter={() => setFilaEncima(row.id)}
                    onMouseLeave={() => setFilaEncima((v) => (v === row.id ? null : v))}
                  >
                    <Barra
                      row={row}
                      layout={layout}
                      activa={filaActiva === row.id}
                      fantasma={fantasmas[indicePorId[row.id]]}
                      propuesta={propuesta?.[row.task_id]}
                      arrastrando={arrastre?.id === row.id}
                      deltaPx={(arrastre?.deltaDias || 0) * eje.px}
                      modoArrastre={arrastre?.modo}
                      pctEnVivo={arrastre?.id === row.id ? dedicacionEnVivo : null}
                      vinculando={vinculando}
                      puedeArrastrar={puedeArrastrar && !propuesta}
                      onHover={alPasar}
                      onSalir={alSalir}
                      onClick={onRowClick}
                      onIniciarArrastre={iniciar}
                      onArmarVinculo={armarVinculo}
                      onCompletarVinculo={completarVinculo}
                    />
                  </div>
                );
              })}
              {/*
                **El SVG dejo de ser `pointer-events-none` el 2026-10-08.** Un vinculo se creaba
                con dos toques y **no se podia borrar**: para deshacer habia que ir al tablero
                del caso. Dibujar una flecha que no se puede sacar de donde se puso es la mitad
                de un gesto.

                El `pointer-events: none` esta en el `<svg>` y se enciende en cada `<path>`: asi
                el lienzo entero no roba clics a las barras, y la flecha —que es una linea de
                1 px— se puede tocar por su propia zona ancha e invisible.

                **Y va despues de las barras, no antes.** Estaba antes, y el contenedor de cada
                fila ocupa **todo el ancho**: tapaba las flechas y se comia los clics. Por eso el
                SVG nacio `pointer-events-none` — era lo unico que lo hacia inofensivo estando
                debajo. Medido en el navegador: cliquear exactamente sobre el trazo no abria
                nada. Dibujarlo arriba, con el lienzo transparente a los clics y solo el trazo
                sensible, es lo que hace que las dos cosas convivan.
              */}
              <svg className="absolute inset-0 pointer-events-none" width={eje.ancho} height={alto}>
                <defs>
                  <marker id="flecha" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#a1a1aa" />
                  </marker>
                </defs>
                {flechas.map((f) => (
                  <g key={f.key}>
                    {/* La zona de agarre: 10 px invisibles sobre el mismo trazo. Una linea de
                        1 px es imposible de acertar con el mouse. */}
                    {f.linkId && onQuitarVinculo && (
                      <path
                        d={f.d} fill="none" stroke="transparent" strokeWidth="10"
                        className="pointer-events-stroke cursor-pointer"
                        style={{ pointerEvents: 'stroke' }}
                        onClick={() => onQuitarVinculo(f)}
                        data-testid={`gantt-flecha-${f.linkId}`}
                      >
                        <title>{f.titulo}</title>
                      </path>
                    )}
                    <path
                      d={f.d} fill="none" stroke="#a1a1aa" strokeWidth="1"
                      strokeDasharray={f.punteada ? '3 2' : undefined}
                      markerEnd="url(#flecha)"
                    />
                    {/* La sigla solo cuando **no** es la de siempre, y el lag solo cuando hay:
                        rotular las cuatro con su sigla llenaria el grafico de letras para decir
                        lo que ya se da por supuesto. */}
                    {(f.punteada || f.lag !== 0) && (
                      <text
                        x={f.lx + 3} y={f.ly - 2} fontSize="7" fill="#71717a"
                        className="pointer-events-none select-none font-mono"
                      >
                        {f.punteada ? f.sigla : ''}{f.lag ? `${f.lag > 0 ? '+' : ''}${f.lag}d` : ''}
                      </text>
                    )}
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* ── carga ── */}
      {verCarga && (
        <div className="border-t border-brand-border">
          <div className="flex">
            <div className="shrink-0 border-r border-brand-border bg-zinc-50" style={{ width: ANCHO_NOMBRES }}>
              <div
                className="px-2 border-b border-brand-border text-[9px] font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center"
                style={{ height: ALTO_CABECERA_CARGA }}
              >
                Carga · % del día hábil
              </div>
              {personas.length === 0 && (
                <div className="px-2 flex items-center text-[10px] text-zinc-400" style={{ height: ALTO_DE_PERSONA }}>
                  Sin trabajo asignado en el rango.
                </div>
              )}
              {personas.map((p) => (
                <div key={p.persona_id} className="flex items-center gap-1.5 px-2 border-b border-zinc-50" style={{ height: ALTO_DE_PERSONA }}>
                  {p.pico_pct > 100 && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: COLOR.peligro }} />}
                  <span className="font-mono text-[10px] truncate">{p.persona_id}</span>
                  <span className={`ml-auto text-[9px] shrink-0 ${p.pico_pct > 100 ? 'text-brand-danger' : 'text-zinc-400'}`}>
                    {p.pico_pct ? `${p.pico_pct}%` : '—'}
                  </span>
                </div>
              ))}
            </div>
            <div
              ref={cargaRef}
              className="overflow-x-auto overflow-y-hidden flex-1"
              onScroll={() => sincronizar(cargaRef, planRef)}
            >
              <div style={{ width: eje.ancho }}>
                <div className="border-b border-brand-border" style={{ height: ALTO_CABECERA_CARGA }} />
                <div className="relative" style={{ height: Math.max(ALTO_DE_PERSONA, personas.length * ALTO_DE_PERSONA) }}>
                <Fondo eje={eje} alto={Math.max(ALTO_DE_PERSONA, personas.length * ALTO_DE_PERSONA)} hoyX={hoyX} />
                {personas.map((p, fila) => (
                  <div key={p.persona_id} className="absolute left-0 right-0 border-b border-zinc-50" style={{ top: fila * ALTO_DE_PERSONA, height: ALTO_DE_PERSONA }}>
                    {/* La linea de capacidad. Que la barra la cruce es lo que dice "no entra",
                        antes que cualquier color: se ve igual en escala de grises. */}
                    <div className="absolute left-0 right-0 border-t border-dashed border-zinc-300" style={{ bottom: 8 }} />
                    {(p.dias || []).filter((d) => d.pct > 0).map((d) => {
                      const x = eje.xDe(d.dia);
                      if (x === null) return null;
                      const altoBarra = Math.min(20, Math.round((d.pct / 100) * 14));
                      return (
                        <div
                          key={d.dia}
                          title={`${p.persona_id} · ${d.dia} · ${d.pct}% (${d.horas} h)`}
                          className="absolute"
                          style={{
                            left: x + 1, width: Math.max(2, eje.px - 2), bottom: 2, height: altoBarra,
                            background: colorDeCarga(d.pct),
                            backgroundImage: d.conflicto
                              ? 'repeating-linear-gradient(135deg,rgba(255,255,255,.6) 0 2px,transparent 2px 5px)'
                              : undefined,
                            borderTop: d.conflicto ? `2px solid ${COLOR.aviso}` : undefined,
                          }}
                          data-testid={d.conflicto ? `gantt-carga-conflicto-${p.persona_id}` : undefined}
                        />
                      );
                    })}
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <GanttConflictos carga={carga} />
      <GanttBarraCard row={encima?.row} rect={encima?.rect} horasPorDia={meta?.horas_por_dia} />
    </div>
  );
}
